import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import Loader from "../../Shared/Loader/Loader";
import APIConstant from "../../../utils/pathConstants";
import api from "../../../utils/apiClient";
import CommonConfig from '../../../utils/constant';
import Toast from '../../Shared/Toast/Toast';
import HallTicketCard from './HallTicketCard';
import { useCatchResponse } from '../../../utils/useCatchResponse';

const HallTicketGenerator = () => {
    const { catchResponse } = useCatchResponse();
    const [classList, setClassList] = useState([]);
    const [classId, setClassId] = useState('');
    const [examScheduleList, setExamScheduleList] = useState([]);
    const [examScheduleId, setExamScheduleId] = useState('');
    const [studentsList, setStudentsList] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [blocks, setBlocks] = useState({});
    const [applyBlock, setApplyBlock] = useState('');
    const [visible, setVisible] = useState(false);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        Name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [selectedYear, setSelectedYear] = useState('');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [securityUserID, setSecurityUserID] = useState("");
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        getFinancialYearData(loginData?._id);
    }, []);

    const getFinancialYearData = async (id) => {
        const years = await CommonConfig.getFinancialYears(id);
        const currentYear = CommonConfig.getCurrentFinancialYear(years);
        const selectedAcYear = CommonConfig.getCookie('selectedAcademicYear');
        setSelectedYear(selectedAcYear ?? currentYear?.value);
        await GetClassList(id, selectedAcYear ?? currentYear?.value);
    }

    const onPageChange = (e) => { setFirst(e.first); setRows(e.rows); };

    const GetClassList = async (id, academicYearId) => {
        try {
            let data = { UserID: id, FinancialYearId: academicYearId };
            await api.post(APIConstant.path.getClassList, data).then(async response => {
                if (response.success) {
                    setClassList(response?.data?.filter(x => x?.Status === 'Active')?.map(x => ({
                        ...x,
                        label: `${x?.ClassName}-${x?.SectionFullName}-${x?.MediumName}-${x?.Board}`,
                        value: x?._id
                    })));
                    Loader.hide();
                }
            }).catch(err => { Loader.hide(); catchResponse(err); });
        } catch (err) { catchResponse(err); }
    }

    const onChangeClass = async (classDetailId) => {
        setClassId(classDetailId);
        setExamScheduleId('');
        setBlocks({});
        if (!classDetailId) { setStudentsList([]); setSelectedStudents([]); setExamScheduleList([]); return; }
        Loader.show();
        try {
            const [stuRes, schRes] = await Promise.all([
                api.post(APIConstant.path.GetClassConfigStudentDetail, { ClassId: classDetailId, FinancialYearId: selectedYear, UserID: securityUserID }),
                api.post(APIConstant.path.GetExamScheduleList, { UserID: securityUserID, FinancialYearId: selectedYear }),
            ]);
            const students = stuRes?.success ? stuRes.data : [];
            setStudentsList(students);
            setSelectedStudents(students);
            const schedules = (schRes?.success ? schRes.data : []).filter(s => String(s?.ClassDetailId) === String(classDetailId));
            setExamScheduleList(schedules.map(s => ({ ...s, label: `${s?.TitleName} (${s?.ExamTypeName || ''})`, value: s?._id })));
        } catch (err) { catchResponse(err); }
        Loader.hide();
    };

    const onChangeExamSchedule = async (id) => {
        setExamScheduleId(id);
        if (!id) { setBlocks({}); return; }
        try {
            const res = await api.post(APIConstant.path.GetHallTicketBlocks, { ExamScheduleId: id });
            const map = {};
            (res?.success ? res.data : []).forEach(r => { if (r?.StudentId) map[r.StudentId] = r?.Block || ''; });
            setBlocks(map);
        } catch (err) { catchResponse(err); }
    };

    const setBlockFor = (studentId, value) => setBlocks(prev => ({ ...prev, [studentId]: value }));

    const applyBlockToAll = () => {
        const target = selectedStudents?.length ? selectedStudents : studentsList;
        setBlocks(prev => {
            const next = { ...prev };
            target.forEach(s => { next[s.id] = applyBlock; });
            return next;
        });
    };

    const onGenerate = () => {
        if (!examScheduleId) { Toast.error({ message: 'Please select an Exam Schedule' }); return; }
        if (!selectedStudents?.length) { Toast.error({ message: 'Please select at least one student' }); return; }
        setVisible(true);
    };

    const onCancel = () => setVisible(false);

    const actionBodyTemplateSrNo = (data, props) => props.rowIndex + 1;
    const blockEditor = (row) => (
        <InputText value={blocks[row.id] || ''} onChange={(e) => setBlockFor(row.id, e.target.value)} placeholder="Block" style={{ width: '120px', height: '32px' }} />
    );

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-id-card"></i>
                    <h2>Hall Ticket Generator</h2>
                </div>
                <div className="p-d-flex p-ai-center p-jc-between" style={{ gap: 8 }}>
                    <div className="searchbar-area e-searchbar-area">
                        <i className="pi pi-search" />
                        <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Global Search" />
                    </div>
                    <Dropdown value={classId} options={classList} onChange={(e) => onChangeClass(e.value)} placeholder="Select Class" filter showClear style={{ minWidth: 200, height: '35px', alignItems: 'center' }} />
                    <Dropdown value={examScheduleId} options={examScheduleList} onChange={(e) => onChangeExamSchedule(e.value)} placeholder="Select Exam Schedule" filter showClear disabled={!classId} style={{ minWidth: 220, height: '35px', alignItems: 'center' }} />
                    {classId && examScheduleId ? <Button icon="pi pi-plus" className="e-h-btn" onClick={onGenerate} /> : null}
                </div>
            </div>

            {classId && (
                <div className="p-d-flex p-ai-center p-mt-2" style={{ gap: 8 }}>
                    <InputText value={applyBlock} onChange={(e) => setApplyBlock(e.target.value)} placeholder="Block (e.g. BLOCK 4)" style={{ width: 200, height: '35px' }} />
                    <Button label="Apply Block to Selected" icon="pi pi-copy" className="p-button-text p-button-sm" style={{ height: '35px' }} onClick={applyBlockToAll} />
                </div>
            )}

            <DataTable
                value={studentsList}
                paginator
                rows={rows}
                first={first}
                filters={filters}
                filterDisplay="menu"
                globalFilterFields={['Name']}
                responsiveLayout="scroll"
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput"
                rowsPerPageOptions={[5, 10, 20]}
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
                onPage={onPageChange}
                selectionMode="checkbox" selection={selectedStudents}
                onSelectionChange={(e) => setSelectedStudents(e.value)}
                className='custom-table'
                scrollHeight='calc(100vh - 160px)'
                size='small'
            >
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                <Column field="id" body={actionBodyTemplateSrNo} header="Sr No." />
                <Column field="Name" header="Student Name" />
                <Column field="RollNo" header="Roll No" />
                <Column field="StudentId" header="Enrollment No" />
                <Column header="Allotted Block" body={blockEditor} />
            </DataTable>

            <Dialog
                header='Generate Hall Ticket'
                className='dialog-popup'
                visible={visible}
                onHide={onCancel}
                draggable={false}
                position="center"
            >
                <HallTicketCard
                    onCancel={onCancel}
                    classDetailId={classId}
                    examScheduleId={examScheduleId}
                    financialYearId={selectedYear}
                    studentsData={selectedStudents.map(s => ({ ...s, Block: blocks[s.id] || '' }))}
                    blocks={blocks}
                />
            </Dialog>
        </div>
    );
};

export default HallTicketGenerator;
